import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Eye,
  Edit2,
  ExternalLink,
  Bot,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  Shield,
  Layers,
} from 'lucide-react';
import Swal from 'sweetalert2';
import ArticleDetailModal from '../components/ArticleDetailModal';
import ArticleEditModal from '../components/ArticleEditModal';
import RejectProposalModal from '../components/RejectProposalModal';
import AuditLogsModal from '../components/AuditLogsModal';
import Pagination from '../components/Pagination';
import NewsAgentControlPanel from '../components/NewsAgentControlPanel';
import {
  getArticles,
  approveArticle,
  rejectArticle,
  getCategories,
} from '../../../services/newsNewsletterApi';

const REGULATORY_STAGE_MAP = {
  NOT_APPLICABLE: { label: 'Non applicable / Général', badge: 'neutral' },
  PROPOSAL: { label: 'Proposition / Projet (ex: PLF)', badge: 'info' },
  ADOPTED: { label: 'Adopté (Loi / Arrêté)', badge: 'warning' },
  IN_FORCE: { label: 'En vigueur (JORT / Applicable)', badge: 'success' },
  AMENDED: { label: 'Modifié / Révisé', badge: 'secondary' },
  REPEALED: { label: 'Abrogé / Expiré', badge: 'danger' },
};

export default function ValidationTab() {
  const [proposals, setProposals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters & Search
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedValidationStatus, setSelectedValidationStatus] = useState('PENDING_REVIEW');
  const [selectedRegulatoryStage, setSelectedRegulatoryStage] = useState('');

  // Pagination state (Server-side)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [kpis, setKpis] = useState({
    total: 0,
    pendingReviewCount: 0,
    publishedCount: 0,
    draftCount: 0,
    rejectedCount: 0,
  });

  // Modals state
  const [detailArticle, setDetailArticle] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [rejectArticleTarget, setRejectArticleTarget] = useState(null);
  const [auditArticleTarget, setAuditArticleTarget] = useState(null);

  // Debounce search input to avoid excessive API requests
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadProposals();
  }, [debouncedSearch, selectedCategory, selectedValidationStatus, selectedRegulatoryStage, page, limit]);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      if (res?.success) {
        setCategories(res.data || []);
      }
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  const loadProposals = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        origin: 'AI_AGENT', // Focus on AI Agent proposals
      };

      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (selectedCategory) params.categoryId = selectedCategory;
      if (selectedValidationStatus) params.validationStatus = selectedValidationStatus;
      if (selectedRegulatoryStage) params.regulatoryStage = selectedRegulatoryStage;

      const res = await getArticles(params);
      if (res?.success) {
        setProposals(res.data || []);
        const pg = res.pagination || {
          total: res.data?.length || 0,
          page,
          limit,
          totalPages: Math.ceil((res.data?.length || 0) / limit) || 1,
        };
        setPagination(pg);
        if (res.kpis) {
          setKpis(res.kpis);
        }
        // Auto-correct page if current page exceeds totalPages
        if (pg.totalPages > 0 && page > pg.totalPages) {
          setPage(pg.totalPages);
        }
      }
    } catch (err) {
      console.error('Erreur chargement propositions agent:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (article) => {
    const confirm = await Swal.fire({
      title: 'Approuver cette proposition ?',
      text: `L'article "${article.title}" sera validé et passera en statut APPROUVÉ (prêt pour publication).`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, approuver',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#38A169',
    });

    if (confirm.isConfirmed) {
      try {
        await approveArticle(article._id);
        Swal.fire('Approuvé', 'La proposition a été approuvée avec succès.', 'success');
        loadProposals();
      } catch (err) {
        Swal.fire('Erreur', err.message || 'Impossible d’approuver l’article', 'error');
      }
    }
  };

  const handleConfirmReject = async (id, reason) => {
    await rejectArticle(id, reason);
    Swal.fire('Rejeté', 'La proposition a été rejetée et le motif a été consigné.', 'success');
    loadProposals();
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="an-tab-container animate-fade-in">
      {/* Panneau de Pilotage & Supervision de l'Agent IA */}
      <NewsAgentControlPanel onProposalsUpdated={loadProposals} />

      {/* KPI Stats Ribbon */}
      <div className="an-stats-row">
        <div className="an-stat-box">
          <div className="an-stat-icon-wrap blue">
            <Bot size={24} />
          </div>
          <div className="an-stat-info">
            <span className="an-stat-num">{pagination.total}</span>
            <span className="an-stat-desc">Propositions Agent ({selectedValidationStatus || 'Toutes'})</span>
          </div>
        </div>

        <div className="an-stat-box">
          <div className="an-stat-icon-wrap orange">
            <AlertTriangle size={24} />
          </div>
          <div className="an-stat-info">
            <span className="an-stat-num">{kpis.pendingReviewCount ?? 0}</span>
            <span className="an-stat-desc">En attente de révision</span>
          </div>
        </div>

        <div className="an-stat-box">
          <div className="an-stat-icon-wrap green">
            <CheckCircle2 size={24} />
          </div>
          <div className="an-stat-info">
            <span className="an-stat-num">{kpis.publishedCount ?? 0}</span>
            <span className="an-stat-desc">Articles validés en ligne</span>
          </div>
        </div>

        <div className="an-stat-box">
          <div className="an-stat-icon-wrap purple">
            <Sparkles size={24} />
          </div>
          <div className="an-stat-info">
            <span className="an-stat-num">
              {proposals.length > 0
                ? Math.round(
                    proposals.reduce((acc, curr) => acc + (curr.aiMetadata?.confidenceScore || 85), 0) /
                      proposals.length
                  )
                : 90}
              %
            </span>
            <span className="an-stat-desc">Score moyen de confiance</span>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="an-filter-bar mt-4">
        <div className="an-search-input-wrapper">
          <Search size={18} className="an-search-icon" />
          <input
            type="text"
            className="an-search-input"
            placeholder="Rechercher par titre, source ou extrait..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="an-filters-group">
          <select
            className="an-filter-select"
            value={selectedValidationStatus}
            onChange={(e) => {
              setSelectedValidationStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tous les statuts de validation</option>
            <option value="PENDING_REVIEW">En attente (PENDING_REVIEW)</option>
            <option value="APPROVED">Approuvés (APPROVED)</option>
            <option value="REJECTED">Rejetés (REJECTED)</option>
          </select>

          <select
            className="an-filter-select"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            className="an-filter-select"
            value={selectedRegulatoryStage}
            onChange={(e) => {
              setSelectedRegulatoryStage(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Tous les stades réglementaires</option>
            {Object.entries(REGULATORY_STAGE_MAP).map(([key, val]) => (
              <option key={key} value={key}>
                {val.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="an-btn-icon-refresh"
            onClick={loadProposals}
            title="Rafraîchir"
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Proposals Data Table */}
      <div className="an-table-card mt-3">
        {loading ? (
          <div className="an-loading-state">Chargement des propositions de l'agent...</div>
        ) : proposals.length === 0 ? (
          <div className="an-empty-state">
            <Bot size={48} className="text-muted mb-3" />
            <h3>Aucune proposition d'article pour le moment</h3>
            <p className="text-muted">
              Dès que l'agent d'ingestion transmettra des dépêches ou projets de textes réglementaires, ils apparaîtront ici pour contrôle et validation humaine.
            </p>
          </div>
        ) : (
          <div className="an-table-responsive">
            <table className="an-table">
              <thead>
                <tr>
                  <th>Titre & Code</th>
                  <th>Source d'origine</th>
                  <th>Catégorie</th>
                  <th>Stade Réglementaire</th>
                  <th>Validation</th>
                  <th>Score IA</th>
                  <th>Collecté le</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((item) => {
                  const regStage = REGULATORY_STAGE_MAP[item.tunisianContext?.regulatoryStage] || {
                    label: item.tunisianContext?.regulatoryStage || 'N/A',
                    badge: 'neutral',
                  };

                  return (
                    <tr key={item._id}>
                      {/* Title & Code */}
                      <td>
                        <div className="an-table-title-cell">
                          <span className="an-code-badge">{item.articleCode}</span>
                          <span className="font-bold text-dark">{item.title}</span>
                          {item.tunisianDomains?.length > 0 && (
                            <div className="an-cell-chips mt-1">
                              {item.tunisianDomains.slice(0, 2).map((d) => (
                                <span key={d} className="an-micro-chip">
                                  {d}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Source Link */}
                      <td>
                        {item.sourceUrl ? (
                          <a
                            href={item.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="an-table-source-link"
                            title={item.sourceUrl}
                          >
                            <span>{item.sourceName || 'Lien source'}</span>
                            <ExternalLink size={12} className="ml-1" />
                          </a>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>

                      {/* Category */}
                      <td>
                        <span className="an-cat-tag">
                          {item.primaryCategoryId?.name || 'Général'}
                        </span>
                      </td>

                      {/* Regulatory Stage */}
                      <td>
                        <span className={`an-badge an-badge-${regStage.badge}`}>
                          {regStage.label}
                        </span>
                      </td>

                      {/* Validation Status */}
                      <td>
                        <span
                          className={`an-badge an-badge-val-${item.validationStatus?.toLowerCase()}`}
                        >
                          {item.validationStatus}
                        </span>
                      </td>

                      {/* Agent Score */}
                      <td>
                        <span className="an-score-badge">
                          <Sparkles size={11} className="mr-1" />
                          {item.aiMetadata?.confidenceScore || 85}%
                        </span>
                      </td>

                      {/* Collection Date */}
                      <td className="text-muted small">{formatDate(item.createdAt)}</td>

                      {/* Actions */}
                      <td className="text-right">
                        <div className="an-table-actions">
                          <button
                            type="button"
                            className="an-action-btn view"
                            onClick={() => setDetailArticle(item)}
                            title="Consulter les détails"
                          >
                            <Eye size={15} />
                          </button>

                          <button
                            type="button"
                            className="an-action-btn edit"
                            onClick={() => setEditArticle(item)}
                            title="Modifier avant approbation"
                          >
                            <Edit2 size={15} />
                          </button>

                          {item.validationStatus === 'PENDING_REVIEW' && (
                            <>
                              <button
                                type="button"
                                className="an-action-btn approve"
                                onClick={() => handleApprove(item)}
                                title="Approuver la proposition"
                              >
                                <CheckCircle2 size={15} />
                              </button>

                              <button
                                type="button"
                                className="an-action-btn reject"
                                onClick={() => setRejectArticleTarget(item)}
                                title="Rejeter avec motif obligatoire"
                              >
                                <XCircle size={15} />
                              </button>
                            </>
                          )}

                          <button
                            type="button"
                            className="an-action-btn audit"
                            onClick={() => setAuditArticleTarget(item)}
                            title="Historique d'audit"
                          >
                            <Shield size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Server-Side Pagination Bar */}
        {pagination.total > 0 && (
          <Pagination
            currentPage={page}
            totalPages={pagination.totalPages || 1}
            totalItems={pagination.total || 0}
            limit={limit}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
            itemLabel="propositions"
            disabled={loading}
          />
        )}
      </div>

      {/* Modals */}
      {detailArticle && (
        <ArticleDetailModal
          isOpen={Boolean(detailArticle)}
          onClose={() => setDetailArticle(null)}
          article={detailArticle}
          onApprove={(art) => handleApprove(art)}
          onReject={(art) => setRejectArticleTarget(art)}
          onEdit={(art) => setEditArticle(art)}
        />
      )}

      {editArticle && (
        <ArticleEditModal
          isOpen={Boolean(editArticle)}
          onClose={() => setEditArticle(null)}
          article={editArticle}
          onSaved={() => loadProposals()}
        />
      )}

      {rejectArticleTarget && (
        <RejectProposalModal
          isOpen={Boolean(rejectArticleTarget)}
          onClose={() => setRejectArticleTarget(null)}
          article={rejectArticleTarget}
          onConfirmReject={handleConfirmReject}
        />
      )}

      {auditArticleTarget && (
        <AuditLogsModal
          isOpen={Boolean(auditArticleTarget)}
          onClose={() => setAuditArticleTarget(null)}
          article={auditArticleTarget}
        />
      )}
    </div>
  );
}
